import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { DGI_CERTIFICATE_FILE_ACCEPT } from "@/services/authService";

import { StyledFileInput } from "@/components/shared/StyledFileInput";
import { useCertificateUpload } from "@/hooks/useCertificateUpload";
import { cn } from "@/lib/utils";

export interface CertificateUploadFormProps {
  tenantRut: string | null;
  onSuccess?: () => void | Promise<void>;
  submitButtonLabel?: string;
  showCancel?: boolean;
  onCancel?: () => void;
  /** Optional prefix for input ids (e.g. "cert-form") for a11y when multiple forms exist. */
  idPrefix?: string;
  /** Merged onto the root form element (e.g. onboarding emphasis). */
  className?: string;
}

export function CertificateUploadForm({
  tenantRut,
  onSuccess,
  submitButtonLabel,
  showCancel = false,
  onCancel,
  idPrefix = "cert",
  className,
}: CertificateUploadFormProps): JSX.Element {
  const { t } = useTranslation();
  const {
    file,
    setFile,
    passphrase,
    setPassphrase,
    uploading,
    submit,
    reset,
    canSubmit,
  } = useCertificateUpload({ tenantRut, onSuccess });

  const fileInputId = `${idPrefix}-p12`;
  const passphraseInputId = `${idPrefix}-passphrase`;

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    void submit();
  };

  const handleCancel = (): void => {
    reset();
    onCancel?.();
  };

  const defaultSubmitLabel = t("tenants.edit.dgi.uploading");
  const displaySubmitLabel = submitButtonLabel ?? t("onboarding.step2Upload");

  return (
    <form
      onSubmit={handleSubmit}
      className={cn("space-y-4 rounded-lg border bg-card p-4", className)}
    >
      <div className="grid gap-2">
        <StyledFileInput
          id={fileInputId}
          accept={DGI_CERTIFICATE_FILE_ACCEPT}
          value={file}
          onChange={setFile}
          disabled={uploading}
          label={t("tenants.edit.dgi.p12Label")}
          hint=".pfx"
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor={passphraseInputId}>
          {t("tenants.edit.dgi.passphraseLabel")}
        </Label>
        <PasswordInput
          id={passphraseInputId}
          placeholder={t("tenants.edit.dgi.passphrasePlaceholder")}
          value={passphrase}
          onChange={(e) => setPassphrase(e.target.value)}
          disabled={uploading}
          autoComplete="off"
        />
      </div>
      <div className="flex justify-end gap-2">
        {showCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={uploading}
            className="transition-colors duration-150"
          >
            {t("tenants.edit.cancel")}
          </Button>
        )}
        <Button
          type="submit"
          disabled={!canSubmit || uploading}
          className="transition-colors duration-150"
        >
          {uploading && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin shrink-0" />
          )}
          {uploading ? defaultSubmitLabel : displaySubmitLabel}
        </Button>
      </div>
    </form>
  );
}
